import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Login screen — full-bleed wedding background, centered "invitation card".
 * Two paths:
 *   - Owners → "Continue with Google" (Emergent OAuth)
 *   - Editors → username + password form (Phase 5 Slice B)
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE GOOGLE AUTH FLOW.
 *
 * Visual: warm-ivory editorial design system (Phase 7.0 — Screen B).
 * Layered shadow + inset gold frame styles the card as a wedding invitation.
 * All testids + behavior + auth flow preserved verbatim.
 */
export default function Login() {
  const { loading, loginWithPassword } = useAuth();
  // Module 6G — studio_slug is REQUIRED for editor login (cross-tenant scope).
  // Pre-fill from localStorage so returning editors don't retype it.
  const [studioSlug, setStudioSlug] = useState(() => {
    try { return localStorage.getItem("ls.auth.studio_slug") || ""; }
    catch (_) { return ""; }
  });
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS.
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!studioSlug.trim() || !username.trim() || !password) {
      setError("Studio URL, username and password required.");
      return;
    }
    setSubmitting(true);
    try {
      const user = await loginWithPassword(
        studioSlug.trim().toLowerCase(), username.trim(), password);
      const next = user?.role === "editor" ? "/my-work" : "/dashboard";
      window.location.href = next;  // hard nav so AuthProvider re-init picks up clean state
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid credentials.");
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Spec input field style — referenced once per input below.
  const fldStyle = {
    background: "#FFF",
    border: "1px solid #E4DCCD",
    borderRadius: 9,
    padding: "13px 14px",
    fontSize: 15,
    color: "var(--ds-text)",
    fontFamily: "var(--ds-body-font)",
    width: "100%",
    outline: "none",
    transition: "border-color .15s, box-shadow .15s",
  };
  const fldFocus = (e) => {
    e.currentTarget.style.borderColor = "#B08D55";
    e.currentTarget.style.boxShadow = "0 0 0 3px rgba(176,141,85,.14)";
  };
  const fldBlur = (e) => {
    e.currentTarget.style.borderColor = "#E4DCCD";
    e.currentTarget.style.boxShadow = "none";
  };
  const labelStyle = {
    display: "block",
    fontSize: 10.5,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "#A39A8B",
    fontWeight: 600,
    marginBottom: 6,
    fontFamily: "var(--ds-body-font)",
  };

  return (
    <div
      data-testid="login-screen"
      className="min-h-screen w-full flex items-center justify-center relative"
      style={{
        backgroundColor: "#1C2419",
        backgroundImage:
          "url('https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1920&q=80')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        padding: "32px 16px",
      }}
    >
      {/* Photo by Jonathan Borba on Unsplash: https://unsplash.com/photos/4mZUajCQXjg
          Replaceable later with a per-studio login background if we add the
          concept; the current image is the fallback for all tenants. */}

      {/* Darkening overlay — radial gradient per spec §B. Sits between photo
          and card; pointer-events-none so taps fall through to the card. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(120% 100% at 50% 0%, rgba(20,28,22,.5) 0%, rgba(18,24,18,.74) 70%, rgba(14,19,14,.9) 100%)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      <div
        className="flex flex-col items-center"
        style={{ position: "relative", zIndex: 2, width: "100%" }}
      >
        <div
          data-testid="login-card"
          className="fade-up"
          style={{
            position: "relative",
            width: "min(452px, 100%)",
            background: "linear-gradient(180deg,#FCF9F2 0%,#F8F3E9 100%)",
            borderRadius: 20,
            padding: "48px 48px 40px",
            boxShadow:
              "0 4px 10px rgba(10,16,12,.2), 0 18px 36px -8px rgba(10,16,12,.4), 0 48px 90px -24px rgba(10,16,12,.62), inset 0 2px 0 rgba(255,255,255,.85), inset 0 -1px 0 rgba(150,118,64,.16)",
          }}
        >
          {/* Inset gold invitation frame */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 13,
              border: "1px solid rgba(176,141,85,.34)",
              borderRadius: 13,
              pointerEvents: "none",
            }}
          />

          {/* Header */}
          <div className="text-center" style={{ position: "relative", marginBottom: 28 }}>
            {/* Gold seal monogram */}
            <div
              aria-hidden="true"
              style={{
                width: 60, height: 60,
                borderRadius: "50%",
                margin: "0 auto 18px",
                background: "linear-gradient(150deg,#C8A063,#A87C42)",
                boxShadow:
                  "0 8px 20px rgba(168,124,66,.42), inset 0 1px 0 rgba(255,255,255,.4)",
                display: "grid",
                placeItems: "center",
                color: "#FFF7E8",
                fontFamily: "var(--ds-num-font)",
                fontSize: 30,
                fontWeight: 500,
                lineHeight: 1,
              }}
            >
              L
            </div>

            <div
              className="kicker"
              data-testid="login-kicker"
              style={{
                color: "var(--ds-accent)",
                fontSize: 10,
                letterSpacing: "0.28em",
                fontWeight: 700,
                marginBottom: 10,
              }}
            >
              LEAD STUDIO
            </div>
            <h1
              data-testid="login-title"
              style={{
                fontFamily: "var(--ds-num-font)",
                fontSize: 46,
                color: "var(--ds-ink)",
                lineHeight: 1.05,
                letterSpacing: "-0.01em",
                fontWeight: 400,
                marginBottom: 14,
              }}
            >
              Sign in
            </h1>

            {/* Gold star divider — two hairlines flanking a 4-point star */}
            <div
              aria-hidden="true"
              className="flex items-center justify-center"
              style={{ gap: 10, marginBottom: 14 }}
            >
              <span style={{ width: 40, height: 1, background: "rgba(194,166,104,.5)" }} />
              <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                <path fill="#C2A668" d="M5 0l1.3 3.7L10 5 6.3 6.3 5 10 3.7 6.3 0 5l3.7-1.3z" />
              </svg>
              <span style={{ width: 40, height: 1, background: "rgba(194,166,104,.5)" }} />
            </div>

            <p
              style={{
                fontSize: 13,
                color: "var(--ds-text-2)",
                maxWidth: 300,
                margin: "0 auto",
                lineHeight: 1.5,
              }}
            >
              Owners use Google. Editors use the username + password issued by the studio.
            </p>
          </div>

          {/* Editor password form */}
          <form
            onSubmit={handlePasswordSubmit}
            data-testid="password-login-form"
            style={{ display: "flex", flexDirection: "column", gap: 15, position: "relative" }}
          >
            <div>
              <label style={labelStyle}>Studio URL</label>
              <input
                type="text" autoComplete="organization"
                value={studioSlug} onChange={(e) => setStudioSlug(e.target.value)}
                style={{ ...fldStyle, textTransform: "lowercase" }}
                onFocus={fldFocus} onBlur={fldBlur}
                data-testid="login-studio-slug-input"
                placeholder="e.g. nuptial"
                disabled={submitting}
              />
            </div>
            <div>
              <label style={labelStyle}>Username</label>
              <input
                type="text" autoComplete="username"
                value={username} onChange={(e) => setUsername(e.target.value)}
                style={fldStyle}
                onFocus={fldFocus} onBlur={fldBlur}
                data-testid="login-username-input"
                placeholder="e.g. ramesh"
                disabled={submitting}
              />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input
                type="password" autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                style={fldStyle}
                onFocus={fldFocus} onBlur={fldBlur}
                data-testid="login-password-input"
                disabled={submitting}
              />
            </div>
            {error && (
              <p
                data-testid="login-error"
                style={{ fontSize: 12, color: "var(--ds-neg)", marginTop: -4 }}
              >
                {error}
              </p>
            )}
            <button
              type="submit" disabled={submitting || loading}
              data-testid="login-submit-button"
              style={{
                width: "100%",
                background: "var(--ds-ink)",
                color: "#F4EFE4",
                border: "none",
                borderRadius: 9,
                padding: "15px",
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: "0.06em",
                boxShadow: "0 8px 18px rgba(27,38,31,.24)",
                cursor: submitting || loading ? "not-allowed" : "pointer",
                opacity: submitting || loading ? 0.6 : 1,
                transition: "background-color .18s ease",
                marginTop: 4,
              }}
              onMouseEnter={(e) => {
                if (!submitting && !loading) e.currentTarget.style.backgroundColor = "var(--ds-ink-hover)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "var(--ds-ink)";
              }}
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>

          {/* OR divider */}
          <div
            className="flex items-center"
            style={{
              gap: 12,
              fontSize: 10,
              letterSpacing: "0.22em",
              color: "var(--ds-text-2)",
              textTransform: "uppercase",
              margin: "22px 0 18px",
              position: "relative",
            }}
          >
            <span style={{ flex: 1, height: 1, background: "#E6DDCD" }} />
            <span>OR</span>
            <span style={{ flex: 1, height: 1, background: "#E6DDCD" }} />
          </div>

          <button
            data-testid="continue-with-google-button"
            onClick={handleGoogleSignIn}
            disabled={loading || submitting}
            className="inline-flex items-center justify-center"
            style={{
              width: "100%",
              background: "#FFF",
              border: "1px solid #E4DCCD",
              borderRadius: 9,
              padding: "14px 18px",
              gap: 10,
              fontSize: 13.5,
              fontWeight: 600,
              color: "var(--ds-ink)",
              cursor: loading || submitting ? "not-allowed" : "pointer",
              opacity: loading || submitting ? 0.6 : 1,
              transition: "background-color .18s ease, border-color .18s ease",
              position: "relative",
            }}
            onMouseEnter={(e) => {
              if (!loading && !submitting) {
                e.currentTarget.style.borderColor = "var(--ds-accent)";
                e.currentTarget.style.background = "#FCFAF5";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#E4DCCD";
              e.currentTarget.style.background = "#FFF";
            }}
          >
            <svg width="17" height="17" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"/>
              <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/>
            </svg>
            Continue with Google
          </button>

          {/* Footer inside card */}
          <div
            className="text-center"
            style={{
              marginTop: 28,
              fontSize: 9.5,
              letterSpacing: "0.2em",
              color: "var(--ds-muted)",
              fontWeight: 600,
              textTransform: "uppercase",
              position: "relative",
            }}
          >
            STUDIO · PRIVATE · INVITE ONLY
          </div>
        </div>

        {/* Below-card link on the dark bg */}
        <div
          className="text-center"
          style={{
            marginTop: 22,
            fontSize: 13,
            color: "rgba(244,230,198,.78)",
            textShadow: "0 1px 2px rgba(0,0,0,.4)",
          }}
        >
          New here?{" "}
          <a
            href="/signup"
            data-testid="login-signup-link"
            style={{
              color: "#F4E6C6",
              textDecoration: "underline",
              textDecorationColor: "rgba(244,230,198,.5)",
              textUnderlineOffset: 3,
              fontWeight: 600,
            }}
          >
            Create a studio →
          </a>
        </div>
      </div>
    </div>
  );
}
